import React from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  Target,
  Zap,
  Users,
  BarChart,
  Trophy,
  Linkedin,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  downAndUp,
  scrollVariantsLeft,
  scrollVariantsRight,
  upAndDown,
} from "../../../constants/TextAnimation";
import { Grid } from "../../ui/Grid";
import { user6 } from "../../../assets";

const WhatWeOffer = () => {
  return (
    <>
      {/* hero */}
      <section className="py-8 md:py-16 lg:pb-24 lg:pt-20">
        <div className="relative pt-16 pb-32 flex content-center items-center justify-center">
          <div
            className="absolute top-0 w-full h-full bg-center bg-cover bg-fixed"
            style={{
              backgroundImage: `url("/images/solutions/9.webp")`,
            }}
          >
            <span className="w-full h-full absolute opacity-60 bg-black"></span>
          </div>

          <div className="container relative mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={downAndUp}
              viewport={{ amount: 0.3 }}
              className="items-center flex flex-wrap"
            >
              <div className="w-full lg:w-6/12 px-4 ml-auto mr-auto text-center mt-24">
                <div className="px-6">
                  <h1 className="text-white font-bold text-4xl md:text-5xl leading-tight">
                    Discover our {""}
                    <span className="gradient-text">story</span>
                  </h1>
                  <p className="mt-4 text-lg text-gray-300">
                    Building global success stories with innovation, integrity,
                    and client-centric solutions.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="pb-10 md:pb-16 lg:pb-20" id="whatweoffers">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={scrollVariantsLeft}
            viewport={{ amount: 0.3 }}
            className="max-w-3xl mx-auto mb-12 text-center"
          >
            <h3 className="text-gold font-semibold text-sm uppercase tracking-wide mb-1">
              OUR BELIEFS
            </h3>
            <h2 className="text-3xl font-bold sm:text-4xl mb-4">
              What we believe in
            </h2>
            <p className="text-gray-400 text-lg">
              Our mission is to empower businesses through innovative technology
              solutions that drive growth and success.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={scrollVariantsRight}
            viewport={{ amount: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12"
          >
            <div className="bg-slate-900/50 p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-6">
                <Heart className="h-6 w-6 text-gold" />
              </div>
              <h3 className="text-xl font-bold mb-4">Transparent Practices</h3>
              <p className="text-gray-400">
                Upholding honesty and clarity in all our dealings.
              </p>
            </div>

            <div className="bg-slate-900/50 p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-6">
                <Target className="h-6 w-6 text-gold" />
              </div>
              <h3 className="text-xl font-bold mb-4">Continuous Improvement</h3>
              <p className="text-gray-400">
                Continuously seeking to redefine our standards and solutions.
              </p>
            </div>

            <div className="bg-slate-900/50 p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-6">
                <Zap className="h-6 w-6 text-gold" />
              </div>
              <h3 className="text-xl font-bold mb-4">Respectful Inclusion</h3>
              <p className="text-gray-400">
                Embracing differences and fostering a culture of respect
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="pb-10 md:pb-16 lg:pb-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={upAndDown}
            viewport={{ amount: 0.3 }}
            className="max-w-xl mx-auto mb-12 text-center"
          >
            <h3 className="text-gold font-semibold text-sm uppercase tracking-wide mb-1">
              CORE VALUES
            </h3>
            <h2 className="text-3xl font-bold sm:text-4xl mb-4">
              Our core values
            </h2>
            <p className="text-gray-400 text-lg">
              Our core values shape our culture and guide our decisions in
              delivering exceptional service to our clients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsLeft}
              viewport={{ amount: 0.3 }}
              className="bg-slate-900/50 p-8 rounded-3xl shadow-lg !rounded-tl-none !rounded-br-none relative overflow-hidden"
            >
              <Grid size={25} />
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-5 bg-gradient-to-b from-secondary to-gold rounded-sm" />
                <h3 className="text-2xl font-bold leading-none text-center">
                  Integrity First
                </h3>
              </div>
              <p className="text-gray-400">
                Ensuring meticulous testing processes to consistently achieve
                flawless software performance and user satisfaction.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsLeft}
              viewport={{ amount: 0.3 }}
              className="bg-slate-900/50 p-8 rounded-3xl shadow-lg !rounded-tl-none !rounded-br-none relative overflow-hidden"
            >
              <Grid size={25} />
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-5 bg-gradient-to-b from-secondary to-gold rounded-sm" />
                <h3 className="text-2xl font-bold leading-none text-center">
                  Innovative Thinking
                </h3>
              </div>
              <p className="text-gray-400">
                Continuously pushing boundaries, we embrace creativity to solve
                challenges and improve services
              </p>
            </motion.div>
            <div className="hidden md:block"></div>
            <div className="hidden md:block"></div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsRight}
              viewport={{ amount: 0.3 }}
              className="bg-slate-900/50 p-8 rounded-3xl shadow-lg !rounded-tl-none !rounded-br-none relative overflow-hidden"
            >
              <Grid size={25} />
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-5 bg-gradient-to-b from-secondary to-gold rounded-sm" />
                <h3 className="text-2xl font-bold leading-none text-center">
                  Client Commitment
                </h3>
              </div>
              <p className="text-gray-400">
                Dedicated to client success, we tailor our strategies to meet
                and exceed their unique needs
              </p>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsLeft}
              viewport={{ amount: 0.3 }}
              className="bg-slate-900/50 p-8 rounded-3xl shadow-lg !rounded-tl-none !rounded-br-none relative overflow-hidden"
            >
              <Grid size={25} />
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-5 bg-gradient-to-b from-secondary to-gold rounded-sm" />
                <h3 className="text-2xl font-bold leading-none text-center">
                  Collaborative Spirit
                </h3>
              </div>
              <p className="text-gray-400">
                We believe in the power of teamwork, fostering a supportive
                environment that encourages collective achievement.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="pb-10 md:pb-16 lg:pb-32">
        <div className="flex flex-col lg:flex-row justify-center gap-10 lg:gap-x-28 px-6 py-12 container mx-auto">
          {/* Left: Image */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={scrollVariantsRight}
            viewport={{ amount: 0.3 }}
            className="w-full lg:w-4/12"
          >
            <img
              src="/images/solutions/6.jpg"
              alt="Team"
              className="rounded-xl shadow-lg object-cover w-full"
            />
          </motion.div>

          {/* Right: Text Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={scrollVariantsLeft}
            viewport={{ amount: 0.3 }}
            className="w-full lg:w-4/12"
          >
            <h2 className="text-4xl font-bold mb-4 gradient-text">
              Driving Growth
              <br />
              and Excellence
            </h2>
            <p className="text-gray-400 mb-8">
              We empower clients to build their business by leveraging our
              strong network and professional expertise
            </p>

            {/* Steps */}
            <div className="space-y-6">
              {[
                "Connect with a Strong Network",
                "Discover Business Opportunities",
                "Get Support from Professionals",
              ].map((text, idx) => (
                <div
                  key={idx}
                  className="flex items-center space-x-4 border-b border-slate-700/50 pb-4"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-600/50 text-gray-200 flex items-center justify-center font-semibold">
                    {idx + 1}
                  </div>
                  <p className="font-medium text-gray-400">{text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="pb-10 md:pb-16 lg:pb-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={scrollVariantsLeft}
            viewport={{ amount: 0.3 }}
            className="max-w-3xl mx-auto mb-12 text-center"
          >
            <h3 className="text-gold font-semibold text-sm uppercase tracking-wide mb-1">
              OUR IMPACT
            </h3>
            <h2 className="text-3xl font-bold sm:text-4xl mb-4">
              Driving Growth Through Innovation
            </h2>
            <p className="text-gray-400 text-lg">
              We help businesses achieve remarkable results through digital
              transformation and innovative solutions.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={scrollVariantsLeft}
            viewport={{ amount: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-4">
                <Users className="h-8 w-8 text-gold" />
              </div>
              <div className="text-4xl font-bold text-slate-300 mb-2">30+</div>
              <div className="text-gray-400">Employees</div>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-4">
                <BarChart className="h-8 w-8 text-gold" />
              </div>
              <div className="text-4xl font-bold text-slate-300 mb-2">5</div>
              <div className="text-gray-400">Timezones</div>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-4">
                <Trophy className="h-8 w-8 text-gold" />
              </div>
              <div className="text-4xl font-bold text-slate-300 mb-2">3</div>
              <div className="text-gray-400">Continents</div>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-4">
                <Zap className="h-8 w-8 text-gold" />
              </div>
              <div className="text-4xl font-bold text-slate-300 mb-2">100%</div>
              <div className="text-gray-400">Agile</div>
            </div>
          </motion.div>
        </div>
      </section>

      <main className="container mx-auto px-4 py-12 min-h-screen ">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Text Section */}
          <motion.div
            className="md:col-span-7"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="px-4 sm:p-8">
              <motion.h6
                className="text-4xl sm:text-5xl md:text-6xl font-bold mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <span className="relative inline-block text-white text-3xl font-bold lg:text-4xl tracking-wide">
                  Founder and CEO
                  <span className="absolute left-1 bottom-[-0.625rem] w-full h-1 rounded-[3.125rem] bg-gradient-to-r from-purple-500 to-yellow-500" />
                </span>
              </motion.h6>

              <motion.div
                className="text-2xl md:text-5xl font-bold text-white"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                Wahaj Ahmed{" "}
                <span className="text-sm md:text-sm text-white/70 font-semibold mb-10">
                  CPA, CGA (Canada), FCCA (UK)
                </span>
              </motion.div>

              <motion.p
                className="text-sm md:text-xl text-white/70 inline-flex items-center gap-5 font-semibold mb-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                <span>CEO and Founder, Scan Global Holdings</span>
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  to="https://www.linkedin.com/in/wahaja/"
                >
                  <Linkedin
                    // color="#fff"
                    size={38}
                    className="border-white/20 p-2 rounded-xl bg-gold hover:bg-opacity-85 transition-all duration-200 text-slate-900 border"
                  />
                </Link>
              </motion.p>

              <motion.div
                className="max-w-2xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                <div className="mb-6 flex items-center">
                  <div className="w-12 h-0.5 bg-white mr-4"></div>
                  <p className="text-sm sm:text-base text-gray-200">
                    “ Bringing 15+ years of experience in controlling all
                    aspects of corporate accounting and financial reporting
                    functions of various Fortune 500 Organizations and Top 10
                    global accounting firms. Wahaj leads with extensive
                    experience in global{" "}
                    <span className="font-bold">
                      Business Advisory and Real Estate.
                    </span>
                  </p>
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                  {[
                    {
                      title: "Discovery & Insight",
                      desc: "Every partnership begins with understanding. Through an in-depth consultation, Wahaj and his team identify the client’s goals, challenges, and long-term vision. This stage focuses on clarity uncovering true needs and opportunities that may lie beyond the surface.",
                    },
                    {
                      title: "Strategic Design & Resource Alignment",
                      desc: "Once clarity is achieved, Team Scan orchestrates the right people, tools, and strategies. This is where innovation meets structure where customized solutions are crafted and aligned to combine core strengths to design bespoke solutions in business growth, and investment strategy.",
                    },
                    {
                      title: "Integration & Growth",
                      desc: "In the final phase, every element comes together. Wahaj and his team ensure execution with precision, building exceptional cross-disciplinary collaboration across Business Brokerage, CFO & Financial Solutions, and Advisory Services and real estate. Clients leave not just with results, but with a holistic strategy for continued expansion.",
                    },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      className="relative pl-6 border-l-2 border-gray-600"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + index * 0.2, duration: 0.6 }}
                    >
                      <div className="absolute left-0 top-1.5 w-3 h-3 bg-yellow-500 rounded-full transform -translate-x-1.5"></div>
                      <h3 className="font-medium mb-2 text-white">
                        {item.title}
                      </h3>
                      <p className="text-gray-400">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Image Section */}
          <motion.div
            className="md:col-span-5 relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="md:sticky md:top-24">
              <div className="relative group">
                <motion.div
                  className="relative border-4 border-white shadow-lg bg-white max-w-xs mx-auto h-full"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src="/images/users/wahaj3.jpeg"
                    alt="Profile"
                    className="w-full h-full lg:h-[450px] object-cover"
                  />
                  <motion.div
                    className="absolute top-0 right-0 transform translate-x-3 -translate-y-2"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-yellow-500"></div>
                  </motion.div>
                  <div className="absolute right-0 bottom-0 w-2 bg-yellow-500 h-16"></div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <section className="pb-10 md:pb-16 lg:pb-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={scrollVariantsLeft}
            viewport={{ amount: 0.3 }}
            className="max-w-xl mx-auto mb-12 text-center"
          >
            <h3 className="text-gold font-semibold text-sm uppercase tracking-wide mb-1">
              OUR Clients
            </h3>
            <h2 className="text-3xl font-bold sm:text-4xl mb-4">
              Meet our leadership
            </h2>
            <p className="text-gray-400 text-lg">
              Discover the visionary leaders behind Scan Global Solutions, each
              bringing unique expertise and strategic foresight to steer us
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={scrollVariantsRight}
            viewport={{ amount: 0.1, once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 mx-5 md:mx-10 lg:mx-40 gap-8 mt-12"
            style={{ animation: "fadeIn 0.5s ease-out forwards 2s" }}
          >
            <div className="bg-slate-900/50 border border-slate-200/5 rounded-lg shadow-lg overflow-hidden group">
              <div className="overflow-hidden">
                <img
                  src={user6}
                  alt="CEO"
                  className="w-full h-auto max-h-96 object-cover transform transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold mb-1">
                  Wahaj Ahmed
                </h3>
                <span className="text-gray-400 block text-sm sm:text-[15px]">
                  CPA, CGA (Canada), FCCA (UK)
                </span>
                <p className="text-gold mb-4 text-sm sm:text-base">
                  Chief Executive Officer
                </p>
                <p className="text-gray-400 text-justify text-sm sm:text-base">
                  Bringing 15+ years from Fortune 500 companies, Wahaj leads
                  with extensive experience in global real estate and business
                  advisory.
                </p>
              </div>
            </div>

            {/* <div className="bg-slate-900/50 rounded-lg shadow-lg overflow-hidden group">
              <div className="overflow-hidden">
                <img
                  src="/images/users/user7.jpg"
                  alt="Head of Client Relationship"
                  className="w-full h-auto max-h-64 object-cover transform transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold mb-2">
                  Summer Assaf
                </h3>
                <span className="text-gray-400 mb-4 block text-sm sm:text-[15px]">
                  MBA, BBA
                </span>
                <p className="text-gold mb-4 text-sm sm:text-base">
                  Head of Client Relationship
                </p>
                <p className="text-gray-400 text-justify text-sm sm:text-base">
                  With deep market knowledge and 17+ years of experience, Summer
                  helps clients invest in Dubai’s real estate with clarity and
                  confidence.
                </p>
              </div>
            </div> */}

            {/* <div className="bg-slate-900/50 rounded-lg border border-slate-200/5 shadow-lg overflow-hidden group">
              <div className="overflow-hidden">
                <img
                  src="/images/users/user6.jpg"
                  alt="Managing Partner"
                  className="w-full h-auto max-h-64 object-cover transform transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold mb-2">
                  Abdullah Arif
                </h3>
                <span className="text-gray-400 mb-4 block text-sm sm:text-[15px]">
                  IFA, AIPA
                </span>
                <p className="text-gold mb-4 text-sm sm:text-base">
                  Managing Partner
                </p>
                <p className="text-gray-400 text-justify text-sm sm:text-base">
                  With over 9 years in financial consultancy focusing on IFRS,
                  Abdullah has served prestigious institutions and REITs in
                  Dubai, Dammam, and Toronto.
                </p>
              </div>
            </div> */}

            <div className="bg-slate-900/50 rounded-lg border border-slate-200/5 shadow-lg overflow-hidden group">
              <div className="overflow-hidden">
                <img
                  src="/images/users/user8.png"
                  alt="Marketing Manager"
                  className="w-full h-auto max-h-96 object-cover transform transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold mb-1">
                  Hannah Sioco
                </h3>
                <span className="text-gray-400 block text-sm sm:text-[15px]">
                  B.Com
                </span>
                <p className="text-gold mb-4 text-sm sm:text-base">
                  Marketing Manager
                </p>
                <p className="text-gray-400 text-justify text-sm sm:text-base">
                  Holding a degree in Commerce from University of Toronto,
                  Hannah brings over 5 years of expertise in content creation
                  and strategic marketing solutions.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="pb-10 md:pb-16 lg:pb-32 ">
        <div className="container mx-auto">
          <motion.h6
            initial="hidden"
            whileInView="visible"
            variants={scrollVariantsRight}
            viewport={{ amount: 0.3 }}
            className="font-semibold text-white pb-8 text-center"
          >
            <span className="relative inline-block text-white text-lg lg:text-2xl mb-6 tracking-wide">
              Our Stats
              {/* Gradient underline like ::before */}
              <span className="absolute left-1 bottom-[-0.625rem] w-full h-1 rounded-[3.125rem] bg-gradient-to-r from-secondary to-gold" />
            </span>
          </motion.h6>
          <div className="grid gap-6 grid-cols-2 sm:gap-12 lg:grid-cols-3 lg:gap-8">
            {/* Stats */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsLeft}
              viewport={{ amount: 0.3 }}
              className="text-center"
            >
              <h4 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-neutral-200">
                Accuracy rate
              </h4>
              <p className="mt-2 sm:mt-3 text-2xl md:text-4xl xl:text-5xl font-bold text-gold">
                99.95%
              </p>
              <p className="mt-1 text-gray-500 dark:text-neutral-500">
                in fulfilling orders
              </p>
            </motion.div>
            {/* End Stats */}

            {/* Stats */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsRight}
              viewport={{ amount: 0.3 }}
              className="text-center"
            >
              <h4 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-neutral-200">
                Startup businesses
              </h4>
              <p className="mt-2 sm:mt-3 text-2xl md:text-4xl xl:text-5xl font-bold text-gold">
                2,000+
              </p>
              <p className="mt-1 text-gray-500 dark:text-neutral-500">
                partner with Preline
              </p>
            </motion.div>
            {/* End Stats */}

            {/* Stats */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsLeft}
              viewport={{ amount: 0.3 }}
              className="text-center"
            >
              <h4 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-neutral-200">
                Happy customer
              </h4>
              <p className="mt-2 sm:mt-3 text-2xl md:text-4xl xl:text-5xl font-bold text-gold">
                85%
              </p>
              <p className="mt-1 text-gray-500 dark:text-neutral-500">
                this year alone
              </p>
            </motion.div>
            {/* End Stats */}
          </div>
        </div>
      </section>
      {/* End Features */}
    </>
  );
};

export default WhatWeOffer;
